import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { FileConversionService } from '../services/file-conversion.service';
import { DataSharingService } from '../services/data-sharing.service';
import { FileClientService, FileClientData } from '../services/fileClient.service';
import { environment } from '../environment.ts/environment';

@Component({
  selector: 'app-success',
  standalone: true,
  imports: [CommonModule, HeaderComponent, FooterComponent],
  templateUrl: './success.component.html',
  styleUrl: './success.component.scss'
})
export class SuccessComponent implements OnInit {
  filename: string = '';
  email: string = '';
  amount: string = '0.50';
  isUploading: boolean = false;
  uploadProgress: number = 0;
  uploadMessage: string = '';
  isConverting: boolean = false;
  conversionProgress: number = 0;
  conversionMessage: string = '';
  downloadUrl: string = '';
  isSavingToDatabase: boolean = false;
  saveMessage: string = '';
  Math = Math; // Pour utiliser Math dans le template

  constructor(
    private fileConversionService: FileConversionService,
    private dataSharingService: DataSharingService,
    private fileClientService: FileClientService,
    private router: Router,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    // Définir le titre de la page (42 caractères - optimal)
    this.title.setTitle('Conversion réussie – pdf-to-word.fr');
    
    // Définir les meta tags SEO (160 caractères - optimal)
    this.meta.updateTag({ name: 'description', content: 'Votre conversion PDF en Word est terminée avec succès. Téléchargez votre fichier converti ou recevez-le par email. Service rapide et sécurisé.' });
    this.meta.updateTag({ name: 'keywords', content: 'conversion PDF réussie, télécharger PDF Word, fichier converti pdf-to-word, téléchargement document Word' });
    
    // Meta tags supplémentaires pour le SEO
    this.meta.updateTag({ property: 'og:title', content: 'Conversion réussie – pdf-to-word.fr' });
    this.meta.updateTag({ property: 'og:description', content: 'Votre conversion PDF en Word est terminée avec succès. Téléchargez votre fichier converti ou recevez-le par email. Service rapide et sécurisé.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });

    // Envoyer un événement GTM pour la page de succès
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'success_page_view',
        page_path: '/success',
        page_title: 'Conversion réussie'
      });
    }

    // Récupérer les données depuis les services
    const conversionData = this.fileConversionService.getConversionData();
    const prospectData = this.dataSharingService.getProspectData();

    if (conversionData && conversionData.filename) {
      this.filename = conversionData.filename.replace(
        conversionData.originalFormat ?? '.pdf', 
        conversionData.targetFormat ?? '.docx'
      );
    }

    if (prospectData && prospectData.email) {
      this.email = prospectData.email;
    }

    // Si pas de données, rediriger vers la page d'accueil
    if (!this.filename && !this.email) {
      this.router.navigate(['/']);
      return;
    }

    // Upload du fichier à l'initialisation
    this.uploadFileAndConvert();
  }

  uploadFileAndConvert(): void {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadMessage = 'Préparation de l\'upload...';

    // Simuler une progression pour l'interface utilisateur
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += Math.random() * 10;
        this.uploadMessage = `Upload en cours... ${Math.round(this.uploadProgress)}%`;
      }
    }, 200);

    this.fileConversionService.uploadCurrentFile().subscribe({
      next: (response) => {
        clearInterval(progressInterval);
        this.uploadProgress = 100;
        this.uploadMessage = 'Upload terminé avec succès!';
        
        setTimeout(() => {
          this.isUploading = false;
          this.uploadMessage = '';
        }, 1000);

        const filename = response.data.fileName;
        const originalName = response.data.originalName;
        console.log('Upload réussi - Filename:', filename);

        // Lancer la conversion du fichier après l'upload
        this.convertFile(filename, originalName);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.isUploading = false;
        this.uploadMessage = 'Erreur lors de l\'upload du fichier';
        console.error('Erreur upload:', error);
      }
    });
  }

  convertFile(filename: string, originalName: string): void {
    this.isConverting = true;
    this.conversionProgress = 0;
    this.conversionMessage = 'Conversion du fichier en cours...';

    // Simuler une progression pour l'interface utilisateur
    const progressInterval = setInterval(() => {
      if (this.conversionProgress < 90) {
        this.conversionProgress += Math.random() * 10;
        this.conversionMessage = `Conversion en cours... ${Math.round(this.conversionProgress)}%`;
      }
    }, 200);

    this.fileConversionService.convertFile(filename).subscribe({
      next: (response) => {
        clearInterval(progressInterval);
        this.conversionProgress = 100;
        this.conversionMessage = 'Conversion terminée avec succès!';
        
        setTimeout(() => {
          this.isConverting = false;
          this.conversionMessage = '';
        }, 1000);

        this.downloadUrl = environment.fileConverterBaseUrl + response.data.downloadUrl;
        console.log('Téléchargement du fichier:', this.downloadUrl);

        // Envoi de l'email avec le fichier converti
        this.fileConversionService.sendConvertedEmail({
          to: this.email,
          name: this.filename,
          filePath: this.downloadUrl,
          siteUrl: 'pdf-to-word.fr',
          siteName: 'PDF to Word',
        }).subscribe();

        // Enregistrer le fichier converti en base de données
        this.saveFileToDatabase(response.data.convertedFileName);
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.isConverting = false;
        this.conversionMessage = 'Erreur lors de la conversion du fichier';
        console.error('Erreur conversion:', error);
      }
    });
  }

  copyDownloadLink(): void {
    if (this.downloadUrl) {
      navigator.clipboard.writeText(this.downloadUrl).then(() => {
        // Optionnel : afficher un message de confirmation
        console.log('Lien copié dans le presse-papiers');
      }).catch(err => {
        console.error('Erreur lors de la copie du lien:', err);
      });
    }
  }

  /**
   * Sauvegarde le fichier converti en base de données
   * @param filename - Nom du fichier converti
   * @param filepath - Chemin/URL du fichier
   */
  saveFileToDatabase(filepath: string): void {
    this.isSavingToDatabase = true;
    this.saveMessage = 'Sauvegarde en base de données...';

    // Récupérer les données du prospect pour obtenir l'id_client
    const clientId = this.dataSharingService.getClientId();
    
    if (!clientId) {
      this.isSavingToDatabase = false;
      this.saveMessage = 'Erreur: Données du prospect manquantes';
      console.error('Données du prospect manquantes pour la sauvegarde');
      return;
    }

    // Préparer les données pour l'insertion
    const fileClientData: FileClientData = {
      id_client: clientId,
      filename: this.filename,
      filepath: filepath
    };

    // Appeler le service pour sauvegarder en base de données
    this.fileClientService.createFileClientWithValidation(fileClientData).subscribe({
      next: (response) => {
        this.isSavingToDatabase = false;
        this.saveMessage = 'Fichier sauvegardé avec succès!';
        
        // Effacer le message après 3 secondes
        setTimeout(() => {
          this.saveMessage = '';
        }, 3000);
      },
      error: (error) => {
        this.isSavingToDatabase = false;
        this.saveMessage = 'Erreur lors de la sauvegarde';
        console.error('Erreur lors de la sauvegarde en base de données:', error);
        
        // Effacer le message d'erreur après 5 secondes
        setTimeout(() => {
          this.saveMessage = '';
        }, 5000);
      }
    });
  }

  onNewConversion(): void {
    // Nettoyer les données et rediriger vers la page d'accueil
    this.fileConversionService.clearConversionData();
    this.dataSharingService.clearAllData();
    this.router.navigate(['/login']);
  }
} 